import React, { Suspense, useEffect } from 'react';
import { RTLLayout } from './components/RTLLayout';
import { ConfigProvider } from './providers/ConfigProvider';
import { ThemeProvider } from './providers/ThemeProvider';
import { RouterProvider } from 'react-router-dom';
import { router } from './routes';
import { ToastProvider } from './providers/ToastProvider';
import { useAuthStore } from './store/auth.store';
import { Box } from '@mui/material';
import { initMixpanel } from './analytics/mixpanel';
import OneSignalService from './services/oneSignal.service';

import AppLoadingOverlay from './components/AppLoadingOverlay';

function App() {
  const { user, initializeAuth, isAppBootstrapping } = useAuthStore();

  // Initialize auth store, Mixpanel, and OneSignal on app mount
  useEffect(() => {
    initializeAuth();
    initMixpanel();

    // Initialize OneSignal after a short delay to ensure DOM is ready
    const timer = setTimeout(() => {
      OneSignalService.initialize();
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  // Setup OneSignal when user is authenticated
  useEffect(() => {
    if (!user?.data?.access?.length) return;

    const organizationId = user.data.access[0].organization_id;
    
    const setupOneSignalUser = async () => {
      if (!OneSignalService.isReady()) return;
      
      await OneSignalService.setExternalUserId(user.id);
      await OneSignalService.setTags({
        organization_id: organizationId,
        user_role: user.data.role || 'unknown',
        is_premium: user.organization?.is_premium ? 'true' : 'false',
        platform: 'web'
      });
      await OneSignalService.registerPlayerWithBackend(organizationId, user.id);
    };

    setupOneSignalUser().catch(console.error);
  }, [user?.id]);

  return (
    <ConfigProvider>
      <ThemeProvider>
        <RTLLayout>
          <ToastProvider>
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
              {isAppBootstrapping && <AppLoadingOverlay />}
              <Suspense fallback={<></>}>
                <RouterProvider router={router} />
              </Suspense>
            </Box>
          </ToastProvider>
        </RTLLayout>
      </ThemeProvider>
    </ConfigProvider>
  );
}

export default App;
